/* zedaro-search.js — zd-search v1.0 (2026-07-28)
 *
 * Storefront search: InstantSearch.js + typesense-instantsearch-adapter,
 * browser -> Typesense direct with a search-only key.
 *
 * Two independent paths, per the zOps identifier-lookup design:
 *  1. Identifier fast path — deterministic, zero-tolerant tiered lookup
 *     (exact -> tw_id zfill(7) -> zero-loose) rendered as a pinned
 *     "Part number matches" panel. All matches shown; user selects (option a).
 *     No typo tolerance is ever applied to identifiers.
 *  2. Keyword search + dynamic facets — facet blocks are mounted at runtime
 *     from whatever f_ and n_ prefixed fields exist in the current result set, so new
 *     crawler tag namespaces appear with zero frontend changes.
 */
(function () {
  "use strict";

  /* Read a <script type="application/json"> block emitted by the section
   * (used for the multiline facet-config settings). Returns "" on any problem. */
  function readJsonScript(id) {
    var el = document.getElementById(id);
    if (!el) return "";
    try { return JSON.parse(el.textContent || '""') || ""; }
    catch (e) { return ""; }
  }

  function init() {
    var root = document.getElementById("zedaro-search");
    if (!root || !window.instantsearch || !window.TypesenseInstantSearchAdapter) return;

    var cfg = {
      host: root.dataset.host,
      port: parseInt(root.dataset.port || "443", 10),
      protocol: root.dataset.protocol || "https",
      key: root.dataset.key,
      proxy: root.dataset.proxy || "",
      loggedIn: root.dataset.loggedIn === "true",
      approved: root.dataset.approved === "true",   /* only approved accounts see prices */
      collection: root.dataset.collection || "products",
      destinations: root.dataset.destinations || "destinations",
      staticDestinations: (function () {
        try { return JSON.parse(root.dataset.staticDestinations || "[]"); }
        catch (e) { return []; }
      })(),
      currency: root.dataset.currency || "CAD",
      perPage: parseInt(root.dataset.perPage || "24", 10),
      showDiscontinued: root.dataset.showDiscontinued === "true",
      showUnlisted: root.dataset.showUnlisted !== "false",
      facetOrder: readJsonScript("zd-facet-order"),
      facetHidden: readJsonScript("zd-facet-hidden"),
      /* zops.facet_config (managed in the zOps admin) is the source of truth;
         the two settings above are the fallback when it is absent. */
      facetConfig: readJsonScript("zd-facet-config"),
    };
    if (!cfg.host || !cfg.key) {
      root.innerHTML = "<p>Search is not configured yet (Typesense host/key missing " +
                       "in the section settings).</p>";
      return;
    }

    // Access control: logged-out visitors use the public-only key immediately.
    // Logged-in customers fetch a per-customer scoped key from the zOps App
    // Proxy (which embeds their private_* access filter, unforgeable). On any
    // failure we fall back to the public key — never an unfiltered one, so
    // private products can never leak. The key is used for BOTH the facet
    // search and the identifier fast-path, so both are access-limited.
    /* EVERY visitor fetches, not just signed-in ones (2026-08-11). The proxy
     * mints a public-only key for a logged-out browser and a scoped one for a
     * customer, both from the CURRENT parent — so the theme carries no key of
     * its own to go stale. It also returns host, port, protocol and
     * collection, making the section settings a fallback for a proxy outage
     * rather than a second copy of the truth. Those five values previously
     * lived in four places (two sections x two themes); when the parent key
     * was replaced, three of them were silently wrong and logged-out search
     * died without a sound. */
    if (cfg.proxy) {
      var box = document.getElementById("zd-searchbox");
      if (box) box.setAttribute("data-loading", "1");

      /* KEY MINT WITH RETRY (2026-08-19). Shopify's app proxy does not reliably
       * attach logged_in_customer_id under NEW customer accounts, so a signed-in
       * shopper can be handed a public (anonymous) key that hides every private
       * product they are entitled to. When Liquid says we ARE signed in but the
       * mint came back anonymous, re-ask a few times — the id is populated on
       * some requests and not others, so a retry usually lands a real key. Never
       * block search on it: after the attempts we proceed with whatever we have. */
      var mintTries = 0;
      var MINT_MAX = (cfg.loggedIn ? 4 : 1);

      function applyKey(data) {
        if (!data) return;
        if (data.key) cfg.key = data.key;
        if (data.ttl) cfg.keyTtl = Number(data.ttl) || 0;
        if (data.host) cfg.host = data.host;
        if (data.port) cfg.port = String(data.port);
        if (data.protocol) cfg.protocol = data.protocol;
        if (data.collection) cfg.collection = data.collection;
      }

      function mint() {
        mintTries++;
        return fetch(cfg.proxy, { credentials: "same-origin", headers: { "Accept": "application/json" } })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (data) {
            applyKey(data);
            if (cfg.loggedIn && data && (data.anonymous || data.degraded) && mintTries < MINT_MAX) {
              return new Promise(function (res) { setTimeout(res, 350 * mintTries); }).then(mint);
            }
          });
      }

      mint()
        .catch(function () { /* fall back to the section settings */ })
        .then(function () {
          if (box) box.removeAttribute("data-loading");
          startSearch(cfg);
        });
    } else {
      startSearch(cfg);
    }
  }

  function startSearch(cfg) {
    var money = new Intl.NumberFormat(undefined, { style: "currency", currency: cfg.currency });

    /* ------------------------------------------------------------------ */
    /* Identifier normalization — mirrors zd_search_facets.py exactly.     */
    /* ------------------------------------------------------------------ */
    function normalizeExact(raw) {
      return String(raw).replace(/[\s\-]+/g, "").toLowerCase();
    }
    function zeroLooseKey(raw) {
      var norm = normalizeExact(raw);
      var stripped = norm.replace(/^0+/, "").replace(/0+$/, "");
      return stripped || norm;
    }

    /* Identifier fast path: one multi_search POST with the three tiers.   */
    var idPanel = document.getElementById("zd-idmatch");
    var idList = document.getElementById("zd-idmatch-list");
    var idAbort = null;

    function identifierLookup(query) {
      var q = normalizeExact(query);
      if (idAbort) idAbort.abort();
      if (!q || q.length < 3) { idPanel.hidden = true; return; }

      var searches = [{ collection: cfg.collection, q: "*",
                        filter_by: "ids_exact:=" + JSON.stringify(q), per_page: 20 }];
      if (/^\d+$/.test(q) && q.length <= 7) {
        searches.push({ collection: cfg.collection, q: "*",
                        filter_by: "ids_exact:=" + JSON.stringify(q.padStart(7, "0")),
                        per_page: 20 });
      }
      searches.push({ collection: cfg.collection, q: "*",
                      filter_by: "ids_loose:=" + JSON.stringify(zeroLooseKey(q)),
                      per_page: 20 });

      idAbort = new AbortController();
      fetch(cfg.protocol + "://" + cfg.host + ":" + cfg.port + "/multi_search", {
        method: "POST",
        signal: idAbort.signal,
        headers: { "Content-Type": "application/json", "X-TYPESENSE-API-KEY": cfg.key },
        body: JSON.stringify({ searches: searches }),
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var results = data.results || [];
          var hits = [];
          for (var i = 0; i < results.length; i++) {  /* first non-empty tier wins */
            if (results[i].hits && results[i].hits.length) { hits = results[i].hits; break; }
          }
          renderIdMatches(hits);
        })
        .catch(function (err) { if (err.name !== "AbortError") idPanel.hidden = true; });
    }

    function renderIdMatches(hits) {
      if (!hits.length) { idPanel.hidden = true; return; }
      idList.innerHTML = hits.map(function (h) {
        var d = h.document;
        return '<li><a href="/products/' + encodeURIComponent(d.handle) + '">' +
          (d.image ? '<img loading="lazy" src="' + d.image + '" alt="">' : "") +
          '<span class="zd-idmatch-title">' + escapeHtml(d.title) + "</span>" +
          '<span class="zd-idmatch-meta">' + escapeHtml(d.vendor || "") +
          " · ZD# " + escapeHtml(d.id) +
          (cfg.approved && typeof d.price === "number" ? " · " + money.format(d.price) : "") +
          "</span></a></li>";
      }).join("");
      idPanel.hidden = false;
    }

    function escapeHtml(s) {
      return String(s).replace(/[&<>"']/g, function (c) {
        return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
      });
    }

    /* ------------------------------------------------------------------ */
    /* Facet labels — mirrors FACET_LABELS in zd_search_facets.py.         */
    /* ------------------------------------------------------------------ */
    var LABELS = {
      f_product_type: "Category", f_product_subtype: "Subcategory",
      f_product_sub_cat: "Group", f_item_category: "Item type",
      f_tool_mat: "Tool material", f_tool_coating: "Coating", f_tool_taper: "Taper",
      f_tool_shank_code: "Shank code",
      n_tool_shank_diam: "Shank diameter (mm)", n_tool_neck_diam: "Neck diameter (mm)",
      n_tool_oal: "Overall length (mm)", n_tool_reach: "Reach (mm)",
      n_tool_loc: "Length of cut (mm)", n_tool_min_ext: "Min extension (mm)",
      n_tool_max_ext: "Max extension (mm)", n_tool_tip_angle: "Tip angle (\u00B0)",
      f_tool_holder_machine_interface_general: "Machine interface",
      f_tool_holder_machine_interface_specific: "Machine interface (specific)",
      f_tool_holder_tool_interface_general: "Tool interface",
      f_tool_holder_tool_interface_specific: "Tool interface (specific)",
      f_machine_interface_size_type: "Interface size/type",
      f_tool_interface_size_type: "Tool interface size/type",
      f_tool_shank_drive_size: "Drive size", f_tool_tol_category: "Tolerance class",
    };
    /* Parse the "facet order" setting: lines of "key" or "key | Custom Label".
     * Blank lines and #comments ignored. Returns {order:[keys], labels:{key:label}}. */
    function parseFacetOrder(text) {
      var order = [], labels = {};
      String(text || "").split(/\r?\n/).forEach(function (raw) {
        var line = raw.trim();
        if (!line || line.charAt(0) === "#") return;
        var parts = line.split("|");
        var key = parts[0].trim();
        if (!key) return;
        order.push(key);
        if (parts.length > 1 && parts[1].trim()) labels[key] = parts[1].trim();
      });
      return { order: order, labels: labels };
    }
    /* ---- managed facet config (zops.facet_config) -------------------- */
    /* Written by the zOps admin's Search Filters page. Shape:
         { categories: { <category>: entry }, default: entry }
         entry = { order:[], hidden:[], labels:{}, showUnlisted:bool }
       The active category is chosen at query time (setManagedFacetCategory);
       until then the default entry applies. Absent/malformed -> fall back to
       the section settings so the page always renders (audit ZOPS-053). */
    var managedDoc = null;
    try {
      managedDoc = typeof cfg.facetConfig === "string"
        ? JSON.parse(cfg.facetConfig || "null") : (cfg.facetConfig || null);
    } catch (e) { managedDoc = null; }

    var facetPlan, hiddenSet, managedShowUnlisted = null;

    function applyFacetConfig(category) {
      var entry = null;
      if (managedDoc && typeof managedDoc === "object") {
        var cats = managedDoc.categories || {};
        entry = (category && cats[category]) || managedDoc["default"] || null;
      }
      if (entry) {
        facetPlan = {
          order: (entry.order || []).map(String),
          labels: (entry.labels && typeof entry.labels === "object") ? entry.labels : {}
        };
        hiddenSet = {};
        (entry.hidden || []).forEach(function (k) {
          k = String(k).trim(); if (k) hiddenSet[k] = 1;
        });
        managedShowUnlisted = entry.showUnlisted !== false;
      } else {
        facetPlan = parseFacetOrder(cfg.facetOrder);
        hiddenSet = {};
        String(cfg.facetHidden || "").split(/\r?\n/).forEach(function (l) {
          var k = l.trim(); if (k && k.charAt(0) !== "#") hiddenSet[k] = 1;
        });
        managedShowUnlisted = null;
      }
    }
    applyFacetConfig(null);

    /* The active category is whichever f_product_type value is refined. It only
       exists AFTER the shopper picks one, so the per-category entry has to be
       re-resolved at that moment and the panel re-planned — resolving once at
       start-up would leave a category entry like "Cutting Tools" saved but never
       applied (audit ZOPS-053). `replanFacets` is installed by the mount step. */
    var activeFacetCategory = null;
    var replanFacets = null;
    function setFacetCategory(category) {
      var next = category || null;
      if (next === activeFacetCategory) return false;
      activeFacetCategory = next;
      applyFacetConfig(next);
      if (typeof replanFacets === "function") replanFacets();
      return true;
    }

    function labelFor(attr) {
      if (facetPlan.labels[attr]) return facetPlan.labels[attr];   /* settings override wins */
      if (LABELS[attr]) return LABELS[attr];
      var base = attr.replace(/^[fn]_/, "").replace(/^routing_(\d+)$/, "Category level $1");
      base = base.replace(/_/g, " ");
      return base.charAt(0).toUpperCase() + base.slice(1);
    }
    /* Panel ordering: routing levels first, then knowns, then discovery order. */
    function priorityFor(attr) {
      var m = attr.match(/^f_routing_(\d+)$/);
      if (m) return parseInt(m[1], 10);                 /* 1..9  */
      if (attr === "f_item_category") return 20;
      if (attr.indexOf("n_") === 0) return 40;          /* dimensions grouped */
      return 30;
    }

    /* ------------------------------------------------------------------ */
    /* InstantSearch                                                        */
    /* ------------------------------------------------------------------ */
    /* THE KEY EXPIRES (2026-08-11). A customer scoped key carries expires_at,
     * and the browser fetches it ONCE at page load. Past that moment every
     * request 401s — which InstantSearch surfaces as a facet click that
     * appears to tick and then reverts, because the search behind it failed
     * and the UI rolled back. The page looked fine on load and broke only
     * after sitting open, which is exactly what "works, then stops working"
     * means here.
     *
     * The fix is to re-fetch before expiry rather than to lengthen the
     * lifetime: a short-lived key is the point (a leaked one self-
     * invalidates), so the client refreshes at 75% of the TTL and hands the
     * new key to the adapter in place. If a refresh fails, the old key stays
     * until the next attempt — no downgrade to a weaker key, ever. */
    function scheduleKeyRefresh(adapterRef) {
      if (!cfg.proxy) return;
      var ttl = cfg.keyTtl || 300;
      var everyMs = Math.max(30, Math.floor(ttl * 0.75)) * 1000;
      setInterval(function () {
        fetch(cfg.proxy, { credentials: "same-origin", headers: { Accept: "application/json" } })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (data) {
            if (!data || !data.key) return;
            /* Never downgrade a signed-in session to a public key on refresh —
             * an intermittent anonymous OR degraded (tag-lookup-failed) mint
             * must not blank a working key. */
            if (cfg.loggedIn && (data.anonymous || data.degraded)) return;
            cfg.key = data.key;
            if (data.ttl) cfg.keyTtl = Number(data.ttl) || cfg.keyTtl;
            if (adapterRef && typeof adapterRef.updateConfiguration === "function") {
              adapterRef.updateConfiguration({
                server: {
                  apiKey: cfg.key,
                  nodes: [{ host: cfg.host, port: Number(cfg.port), protocol: cfg.protocol }],
                  cacheSearchResultsForSeconds: 0,
                },
                additionalSearchParameters: {
                  query_by: QUERY_BY, num_typos: QUERY_TYPOS, prefix: QUERY_PREFIX,
                },
              });
            }
          })
          .catch(function () { /* keep the current key and try again next tick */ });
      }, everyMs);
    }

    /* ------------------------------------------------------------------ *
     * Dimensional + thread search (2026-08-18).
     *
     * query_by additions (arrays below stay POSITION-ALIGNED with query_by):
     *   f_product_type / f_product_subtype — so a category word ("drill")
     *     narrows a dimensional search: "12mm drill" -> 12 mm drills.
     *   f_thread_nom_size — literal thread match: "M6"/"m6" finds the M6 key.
     *     prefix=false + 0 typos on this field keeps M6 off M60 / M8.
     */
    var QUERY_BY     = "title,ids_exact,ids_loose,f_product_type,f_product_subtype,f_thread_nom_size,vendor";
    var QUERY_TYPOS  = "1,0,0,1,1,0,0";
    var QUERY_PREFIX = "true,true,true,true,true,false,true";

    /* Numeric (mm) fields a bare "12mm" is matched against. tool_tip_angle is
     * degrees, not a length, so it is deliberately excluded. */
    var DIM_FIELDS = [
      "n_cutting_diameter", "n_corner_radius",
      "n_tool_shank_diam", "n_tool_neck_diam",
      "n_tool_oal", "n_tool_reach", "n_tool_loc",
      "n_tool_min_ext", "n_tool_max_ext"
    ];
    var DIM_TOL = 0.03;   /* mm; absorbs the ~0.001 mm inch->mm round-trip */

    /* Is this token a dimension? Plain number or inch fraction, optionally
     * suffixed mm / " / in. Alpha-prefixed (M6) and TPI (1/4-20) return null so
     * they stay on the text path. Bare number = mm; inch-marked and bare
     * fractions convert *25.4 (mirrors backend parse_numeric). */
    function dimTokenToMm(tok) {
      var t = String(tok).trim().toLowerCase();
      if (/^m\d/.test(t)) return null;                  /* metric thread */
      if (/^\d+(\/\d+)?-\d+$/.test(t)) return null;      /* imperial thread / TPI */
      var m = t.match(/^(\d+(?:\.\d+)?|\d+\/\d+)(mm|in|")?$/);
      if (!m) return null;
      var isInch = m[2] === '"' || m[2] === "in";
      var isMm = m[2] === "mm";
      var num;
      if (m[1].indexOf("/") !== -1) {
        var p = m[1].split("/");
        num = parseFloat(p[0]) / parseFloat(p[1]);
        if (!isMm) isInch = true;                        /* bare fraction = inch */
      } else {
        num = parseFloat(m[1]);
      }
      if (isNaN(num)) return null;
      return isInch ? num * 25.4 : num;
    }

    /* Pull the FIRST dimensional token out of a query and turn it into a native
     * Typesense filter_by OR'd across every dimension field. Only that one
     * token is consumed; every other word — thread tokens, flute counts, a
     * second dimension — stays in the residual text so nothing is silently
     * dropped (a stray unmatched number is handled by Typesense drop_tokens). */
    function parseDimensionalQuery(query) {
      var tokens = String(query || "").trim().split(/\s+/).filter(Boolean);
      var mm = null, kept = [];
      tokens.forEach(function (tok) {
        if (mm === null) {
          var v = dimTokenToMm(tok);
          if (v !== null) { mm = v; return; }   /* consume the first dimension */
        }
        kept.push(tok);
      });
      if (mm === null) return { residual: query, filter: null };
      var lo = (mm - DIM_TOL).toFixed(3), hi = (mm + DIM_TOL).toFixed(3);
      var clause = "(" + DIM_FIELDS.map(function (f) {
        return f + ":[" + lo + ".." + hi + "]";
      }).join(" || ") + ")";
      return { residual: kept.join(" "), filter: clause };
    }

    var adapter = new TypesenseInstantSearchAdapter({
      server: {
        apiKey: cfg.key,
        nodes: [{ host: cfg.host, port: cfg.port, protocol: cfg.protocol }],
        cacheSearchResultsForSeconds: 60,
      },
      additionalSearchParameters: {
        query_by: QUERY_BY,
        num_typos: QUERY_TYPOS,       /* typos on titles/categories, NEVER on ids or thread */
        prefix: QUERY_PREFIX,
        max_facet_values: 40,
        per_page: cfg.perPage,
        /* MAX_CANDIDATES — the fix for "a filter revealed new results"
           (2026-08-24).

           Searching "ZSG" returned 16 products, of which exactly one was in
           stock. Ticking "In stock only" then returned NINE, eight of which had
           never appeared in the unfiltered list. A filter cannot add results, so
           the unfiltered query was simply missing them.

           The cause is prefix expansion. With prefix search Typesense first
           collects the distinct indexed tokens that start with the typed term,
           and max_candidates caps how many of those it explores — the default is
           4. On this catalogue "zsg" begins a very large number of distinct
           tokens (ZSG-4.MB-G.125, ZSGmini.65, ZSGMINI.WPB.70.22.3MM, ZSG4.PB.G…
           each tokenises differently), so four expansions is nowhere near
           enough: whole families of parts were invisible. Adding a filter forces
           Typesense to search deeper to fill the page, which is why the missing
           products appeared only once the result set was narrowed.

           1000 is far above the number of distinct token variants any real part
           prefix produces here, so expansion is effectively complete while still
           bounded. It costs a little more work per query; correctness on part
           numbers is worth more than that on a catalogue where people search by
           fragment of a code. */
        max_candidates: 1000,
      },
    });

    /* Inject the dimensional filter_by. The adapter concatenates params.filters
     * verbatim into Typesense filter_by and AND-joins it with the slider
     * refinements, so the range facets keep working. Applied to every
     * sub-request (hits + disjunctive facet-count queries) so the counts
     * reflect the dimension too. */
    (function wrapDimensionalSearch() {
      var base = adapter.searchClient;
      var origSearch = base.search.bind(base);
      base.search = function (requests) {
        var rewritten = (requests || []).map(function (req) {
          var params = Object.assign({}, req.params);
          var parsed = parseDimensionalQuery(params.query || "");
          if (parsed.filter) {
            params.query = parsed.residual || "*";       /* "" -> match-all */
            params.filters = params.filters
              ? "(" + params.filters + ") && " + parsed.filter
              : parsed.filter;
          }
          return Object.assign({}, req, { params: params });
        });
        return origSearch(rewritten);
      };
    })();

    /* ------------------------------------------------------------------
       DESTINATIONS — "go here", alongside "buy this" (2026-08-25).

       Searched separately rather than mixed into the product index: they are a
       different KIND of answer, and blending them would let a page title
       outrank a part number, which on a parts catalogue is always the wrong
       trade. They render in their own group ABOVE the products, so a
       destination is offered without ever displacing a part.

       Same scoped key, so the same access filter applies — a private
       collection appears only for its cohort. The static entries (account
       pages, fixed nav) are matched client-side because their URLs come from
       Liquid, not the index.
       ------------------------------------------------------------------ */
    var destBox = document.getElementById("zd-destinations");

    function renderDestinations(items) {
      if (!destBox) return;
      if (!items.length) { destBox.innerHTML = ""; return; }
      destBox.innerHTML =
        '<div class="zd-dest__label">Go to</div>' +
        '<div class="zd-dest__list">' +
        items.map(function (d) {
          return '<a class="zd-dest" href="' + d.url + '">' +
            '<span class="zd-dest__title">' + escapeHtml(d.title) + "</span>" +
            '<span class="zd-dest__kind">' + escapeHtml(d.kind || "") + "</span>" +
            "</a>";
        }).join("") +
        "</div>";
    }

    function staticMatches(q) {
      var needle = String(q || "").trim().toLowerCase();
      if (!needle) return [];
      return (cfg.staticDestinations || []).filter(function (d) {
        return String(d.title || "").toLowerCase().indexOf(needle) !== -1;
      });
    }

    var destTimer = null;
    function searchDestinations(q) {
      if (!destBox) return;
      var query = String(q || "").trim();
      if (query.length < 2) { renderDestinations([]); return; }

      clearTimeout(destTimer);
      destTimer = setTimeout(function () {
        var payload = { searches: [{
          collection: cfg.destinations,
          q: query,
          query_by: "title",
          per_page: 5,
          max_candidates: 1000
        }] };
        fetch(cfg.proxy + "/multi_search", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (data) {
            var hits = ((data && data.results && data.results[0] &&
                         data.results[0].hits) || []).map(function (h) {
              return h.document;
            });
            /* Static first: an exact "Order history" should sit above a
               collection that merely contains the word. */
            renderDestinations(staticMatches(query).concat(hits).slice(0, 6));
          })
          .catch(function () { renderDestinations(staticMatches(query)); });
      }, 150);
    }

    /* Seed from ?q= so a query typed elsewhere ARRIVES here (2026-08-10).
     * The home-page quick search hands off with ?q=<term>, and a customer who
     * bookmarks or shares a search expects the link to reproduce it. Without
     * this the page opened empty and the term had to be retyped, which reads
     * as the hand-off being broken.
     *
     * Read-once, not full InstantSearch routing: routing would also WRITE
     * every refinement into the URL on every click, which changes back-button
     * behaviour across the whole page. Seeding the query alone is the small
     * change that fixes the hand-off; URL-syncing refinements is a separate
     * decision nobody has asked for. */
    var initialQuery = "";
    try {
      initialQuery = (new URLSearchParams(window.location.search).get("q") || "").trim();
    } catch (e) { initialQuery = ""; }

    /* FLASH SALE FILTER (zOps Batch 5, 2026-10-06). The search STARTS with
     * "Flash sale" switched on whenever anything is on a flash sale (switched
     * on after the first render, once a count confirms there is something to
     * show - never an empty page; and not when they arrived with their own
     * ?q= search). A shopper who switches it off keeps it off for the rest of
     * the visit (sessionStorage). */
    var FS_OFF_KEY = "zd_flash_off";
    var flashOff = false;
    try { flashOff = sessionStorage.getItem(FS_OFF_KEY) === "1"; } catch (e) { flashOff = false; }
    var flashWasOn = false;

    var search = instantsearch({
      indexName: cfg.collection,
      searchClient: adapter.searchClient,
      future: { preserveSharedStateOnUnmount: true },
      onStateChange: function (args) {
        var st = (args.uiState && args.uiState[cfg.collection]) || {};
        var on = !!(st.toggle && st.toggle.flash_sale);
        if (flashWasOn && !on) {
          flashOff = true;
          try { sessionStorage.setItem(FS_OFF_KEY, "1"); } catch (e) { /* private mode */ }
        }
        flashWasOn = on;
        args.setUiState(args.uiState);
      },
      initialUiState: initialQuery
        ? (function () { var st = {}; st[cfg.collection] = { query: initialQuery }; return st; })()
        : undefined,
    });

    var w = instantsearch.widgets;
    var baseFilter = cfg.showDiscontinued ? "" : "discontinued:=false";

    search.addWidgets([
      w.configure({
        facets: ["*"],
        maxValuesPerFacet: 40,
        filters: baseFilter,
      }),
      w.searchBox({
        container: "#zd-searchbox",
        placeholder: "Search by part number, ZD#, or keyword\u2026",
        queryHook: function (query, doSearch) {
          identifierLookup(query);
          searchDestinations(query);
          doSearch(query);
        },
      }),
      w.sortBy({
        container: "#zd-sortby",
        items: [
          { label: "Relevance", value: cfg.collection },
          { label: "Price \u2191", value: cfg.collection + "/sort/price:asc" },
          { label: "Price \u2193", value: cfg.collection + "/sort/price:desc" },
        ],
      }),
      w.stats({
        container: "#zd-stats",
        templates: { text: function (d) { return d.nbHits.toLocaleString() + " products"; } },
      }),
      w.clearRefinements({ container: "#zd-clear", templates: { resetLabel: "Clear all" } }),
      w.currentRefinements({
        container: "#zd-current",
        transformItems: function (items) {
          return items.map(function (it) { it.label = labelFor(it.attribute); return it; });
        },
      }),
      w.refinementList({
        container: "#zd-facet-product-type",
        attribute: "f_product_type",
        limit: 20,
        cssClasses: { root: "zd-gate" },
      }),
      w.refinementList({
        container: "#zd-facet-vendor",
        attribute: "vendor",
        limit: 12, showMore: true, searchable: true,
        searchablePlaceholder: "Find vendor\u2026",
      }),
      w.toggleRefinement({
        container: "#zd-facet-flash",
        attribute: "flash_sale",
        templates: { labelText: "Flash sale" },
      }),
      w.toggleRefinement({
        container: "#zd-facet-available",
        attribute: "available",
        templates: { labelText: "In stock only" },
      }),
      ...(cfg.approved ? [w.rangeInput({ container: "#zd-facet-price", attribute: "price" })] : []),
      w.hits({
        container: "#zd-hits",
        templates: {
          empty: "No products match the current filters.",
          item: function (hit) {
            /* The card is a DIV whose upper half is the link, not one big
               anchor (2026-08-24). An Add to cart button cannot live inside an
               <a> — nesting interactive controls is invalid and the click would
               navigate instead of adding. The clickable region is unchanged
               visually; only the markup moved. */
            return '<div class="zd-hit">' +
              '<a class="zd-hit__link" href="/products/' +
              encodeURIComponent(hit.handle) + '">' +
              '<div class="zd-hit__img">' +
              (hit.image ? '<img loading="lazy" src="' + hit.image + '" alt="">' : "") +
              "</div>" +
              (hit.flash_sale ? '<div class="zd-hit__flash">Flash sale</div>' : "") +
              '<div class="zd-hit__title">' + escapeHtml(hit.title) + "</div>" +
              '<div class="zd-hit__meta">' + escapeHtml(hit.vendor || "") +
              " \u00B7 ZD# " + escapeHtml(hit.id) + "</div>" +
              "</a>" +
              '<div class="zd-hit__price">' +
              (cfg.approved && typeof hit.price === "number" ? money.format(hit.price) : "") +
              "</div>" +
              /* Stock status reads as a sentence, not a warehouse code
                 (2026-08-11). "Special order" was both wrong — these are
                 stocked by the vendor, not made to order — and discouraging
                 on a catalogue where most lines are legitimately not held in
                 Cambridge. It is its own line rather than a suffix on the
                 price so the price stays scannable down the column. */
              (hit.available
                ? '<div class="zd-hit__stock zd-hit__stock--in">' +
                  (typeof hit.inventory === "number" && hit.inventory > 0
                    ? hit.inventory + ' in stock \u2014 ready to ship'
                    : 'In stock \u2014 ready to ship') +
                  "</div>"
                : '<div class="zd-hit__stock">Not in stock \u2014 available to order</div>') +
              (hit.min_order > 1
                ? '<div class="zd-hit__stock zd-hit__moq-note">Minimum order ' +
                  hit.min_order + "</div>"
                : "") +
              /* Quantity + Add to cart, approved accounts only — the same gate
                 the price uses, since an unapproved visitor cannot check out and
                 a buy button would only be a dead end. The button carries the
                 HANDLE, not a variant id: the index has no variant, so
                 zedaro-quick-add.js resolves it at click time. */
              (cfg.approved
                ? (function () {
                    /* max_order is the DENY-policy ceiling (0 = no cap);
                       min_order is the vendor minimum (1 = none) — audit
                       ZOPS-126. The box STARTS at the minimum rather than 1,
                       so a cutter first quoted at qty 2 cannot be ordered at 1
                       and priced off a tier that does not exist.

                       A ceiling below the floor means it cannot be ordered at
                       all right now, so the control becomes a link to the
                       product page instead of a box whose min exceeds its max. */
                    var mn = hit.min_order > 1 ? hit.min_order : 1;
                    var mx = hit.max_order > 0 ? hit.max_order : 0;
                    if (mx > 0 && mx < mn) {
                      return '<a class="zd-dest zd-hit__moq" href="/products/' +
                        encodeURIComponent(hit.handle) + '">Minimum ' + mn +
                        " \u00B7 see product</a>";
                    }
                    return '<div class="zd-add-wrap">' +
                      '<input class="zd-add-qty" type="number" inputmode="numeric" ' +
                      'min="' + mn + '" step="1" value="' + mn + '" aria-label="Quantity"' +
                      (mx > 0 ? ' max="' + mx + '"' : "") + '>' +
                      '<button type="button" class="zd-add button" data-label="Add to cart" ' +
                      'data-min="' + mn + '" ' +
                      (mx > 0 ? 'data-max="' + mx + '" ' : "") +
                      'data-handle="' + escapeHtml(hit.handle) + '">Add to cart</button>' +
                      "</div>";
                  })()
                : "") +
              "</div>";
          },
        },
      }),
      w.pagination({ container: "#zd-pagination" }),
    ]);

    /* Dynamic facet mounting: discover f_ and n_ prefixed attributes present in the
     * current results and mount a widget for each on first sight. Blocks are
     * hidden whenever their facet has no values for the active subset
     * (McMaster-style: never show a filter that leads nowhere). */
    var MANUAL = { vendor: 1, f_product_type: 1, price: 1, available: 1, discontinued: 1, flash_sale: 1 };
    var mountedBlocks = {};   // attr -> DOM block
    var dynRoot = document.getElementById("zd-facets-dynamic");

    /* Create one refinement widget for a discovered facet field. Starts hidden;
     * the gate widget reveals it once a Category is chosen and it has values. */
    function mountField(attr, orderIdx) {
      if (MANUAL[attr] || mountedBlocks[attr] || !/^[fn]_/.test(attr)) return;
      var block = document.createElement("div");
      block.className = "zd-facet-block";
      block.dataset.facet = attr;
      block.hidden = true;
      block.style.order = String(orderIdx);
      block.innerHTML = '<h3 class="zd-facet-label">' + escapeHtml(labelFor(attr)) +
                        '</h3><div class="zd-facet-body"></div>';
      dynRoot.appendChild(block);
      var body = block.querySelector(".zd-facet-body");
      mountedBlocks[attr] = block;
      search.addWidgets([
        attr.indexOf("n_") === 0
          ? w.rangeInput({ container: body, attribute: attr, precision: 2 })
          : w.refinementList({ container: body, attribute: attr, limit: 10, showMore: true }),
      ]);
    }

    /* Turn the discovered field list into the final ordered, filtered plan using
     * the section settings: explicit order first, hidden removed, and (if
     * enabled) unlisted facets appended after in the built-in priority order. */
    function planFacets(discovered) {
      var present = {};
      discovered.forEach(function (a) { present[a] = 1; });
      var used = {}, plan = [];
      facetPlan.order.forEach(function (key) {
        if (present[key] && !hiddenSet[key] && !used[key]) { plan.push(key); used[key] = 1; }
      });
      if ((managedShowUnlisted === null ? cfg.showUnlisted : managedShowUnlisted)) {
        discovered.filter(function (a) { return !used[a] && !hiddenSet[a]; })
          .sort(function (a, b) { return priorityFor(a) - priorityFor(b); })
          .forEach(function (a) { plan.push(a); });
      }
      return plan;
    }

    /* Discover which facet fields EXIST by asking Typesense directly (facet_by:*).
     * This is the fix for the chicken-and-egg: InstantSearch only surfaces
     * facets that already have widgets, so we can't discover them from its
     * results — we discover the field names here, then mount widgets for them. */
    function discoverFacetFields() {
      var body = { searches: [{ collection: cfg.collection, q: "*",
                                query_by: "title", facet_by: "*",
                                max_facet_values: 1, per_page: 0 }] };
      return fetch(cfg.protocol + "://" + cfg.host + ":" + cfg.port + "/multi_search", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-TYPESENSE-API-KEY": cfg.key },
        body: JSON.stringify(body),
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var counts = (((data.results || [])[0]) || {}).facet_counts || [];
          return counts.map(function (fc) { return fc.field_name; })
                       .filter(function (name) { return /^[fn]_/.test(name) && !MANUAL[name]; });
        })
        .catch(function () { return []; });   // degrade: basic search still works
    }

    /* Gate widget: reveal the dynamic facets once a Category (f_product_type) is
     * chosen — NOT on vendor — and hide any that have no values in the current
     * subset. Vendor stays a normal, always-available facet. */
    var gateWidget = {
      $$type: "zedaro.facetGate",
      render: function (opts) {
        var results = opts.results;
        if (!results) return;
        var refinements = results.getRefinements ? results.getRefinements() : [];
        var catRefinement = null;
        for (var ri = 0; ri < refinements.length; ri++) {
          if (refinements[ri].attributeName === "f_product_type") {
            catRefinement = refinements[ri]; break;
          }
        }
        var hasCategory = !!catRefinement;
        /* Apply this category's saved facet order/hidden/labels, if it has one. */
        setFacetCategory(hasCategory ? (catRefinement.name || catRefinement.value) : null);
        dynRoot.hidden = !hasCategory;
        if (!hasCategory) return;

        var fmap = {};
        (results.disjunctiveFacets || []).concat(results.facets || [])
          .forEach(function (f) { fmap[f.name] = f; });
        Object.keys(mountedBlocks).forEach(function (attr) {
          var f = fmap[attr];
          var has = !!f && (
            (f.data && Object.keys(f.data).length > 0) ||
            (f.stats && typeof f.stats.max === "number" && f.stats.max !== f.stats.min)
          );
          mountedBlocks[attr].hidden = !has;
        });
      },
    };

    /* Discover -> plan (order/hide/relabel per settings) -> mount -> gate -> start. */
    discoverFacetFields().then(function (discovered) {
      if (window.console && console.info) {
        console.info("[zedaro-search] available facet keys: " +
                     (discovered.slice().sort().join(", ") || "(none)"));
      }
      planFacets(discovered).forEach(function (attr, i) { mountField(attr, i); });

      /* Re-apply the ACTIVE category's plan to blocks that are already mounted.
         Widgets are never torn down — only their order, visibility and titles
         change — so switching category is cheap and cannot lose refinements. */
      replanFacets = function () {
        var plan = planFacets(discovered);
        var inPlan = {};
        plan.forEach(function (attr) { inPlan[attr] = 1; });
        // Order: re-append in plan order; anything hidden by this category's
        // config is hidden rather than removed, so it returns instantly if the
        // shopper switches back.
        plan.forEach(function (attr, i) {
          var block = mountedBlocks[attr];
          if (!block) { mountField(attr, i); block = mountedBlocks[attr]; }
          if (!block) return;
          block.hidden = false;
          // Panel order is CSS flex order, not DOM position — set the same
          // property mountField uses so a re-plan actually reorders.
          block.style.order = String(i);
          var t = block.querySelector(".zd-facet-label");
          if (t) t.textContent = labelFor(attr);
        });
        Object.keys(mountedBlocks).forEach(function (attr) {
          if (!inPlan[attr]) mountedBlocks[attr].hidden = true;
        });
      };

      search.addWidgets([gateWidget]);
      search.start();

      /* Turn the flash-sale filter on at the start - only when something is on
       * sale for THIS shopper (the proxy applies their access) and they have
       * not switched it off this visit. */
      /* Not when the shopper arrived with a search of their own (?q=): a part
       * number typed on the home page must find the part, on sale or not. */
      if (!flashOff && !initialQuery) {
        fetch(cfg.proxy + "/multi_search", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ searches: [{
            collection: cfg.collection, q: "*", query_by: "title", per_page: 0,
            filter_by: (baseFilter ? baseFilter + " && " : "") + "flash_sale:=true"
          }] })
        })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (d) {
            var n = (d && d.results && d.results[0] && d.results[0].found) || 0;
            if (n > 0 && !flashOff) {
              search.setUiState(function (prev) {
                var next = Object.assign({}, prev);
                var cur = Object.assign({}, next[cfg.collection] || {});
                cur.toggle = Object.assign({}, cur.toggle || {}, { flash_sale: true });
                next[cfg.collection] = cur;
                return next;
              });
            }
          })
          .catch(function () { /* no filter - search simply starts unfiltered */ });
      }
    scheduleKeyRefresh(adapter);

    /* queryHook fires on TYPING, not on a seeded initial query, so the
     * identifier fast-path would stay silent for exactly the case it matters
     * most: someone arriving with a part number already in the URL. */
    if (initialQuery) { identifierLookup(initialQuery); searchDestinations(initialQuery); }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
